import { useTheme } from "@emotion/react";
import type { SerializedStyles } from "@emotion/serialize";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, Tabs } from "@aviary";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";

import { FilterTitle } from "../../shared/FilterTitle/FilterTitle";
import { ExcludeIngredientsContent } from "../ExcludeIngredients/ExcludeIngredientsContent";
import { IncludeIngredientsContent } from "../IncludeIngredients/IncludeIngredientsContent";

import * as styles from "./Ingredients.styles";

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  isDefaultExpanded?: boolean;
  isBadge?: boolean;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
}

const Ingredients = ({
  isBold,
  logFilterEvent,
  isDefaultExpanded,
  isBadge,
  customAccordionStyle,
}: Props) => {
  const { t } = useTranslation();
  const currentTheme = useTheme();
  const { activeIngredients, onlyIngredient, excludedIngredients } = useCatalogSearch();
  const [openTabId, setOpenTabId] = useState("include");

  return (
    <Accordion
      css={[styles.accordion, customAccordionStyle]}
      data-e2e="ingredients-filter"
      isDefaultExpanded={isDefaultExpanded || activeIngredients?.length > 0}
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.Ingredients)}
          count={activeIngredients?.length + excludedIngredients?.length}
          isBadge={isBadge}
          isSecondaryWeight={isBold}
        />
      }
    >
      <Tabs isUncloseable initialTabId={openTabId}>
        <Tabs.Headers isFullWidth customHeadersStyle={styles.tabHeaders(currentTheme)}>
          <Tabs.Header
            tabId="include"
            isCustomChild
            onClick={() => setOpenTabId("include")}
            css={[styles.tabHeader, "include" === openTabId && styles.tabSelected]}
          >
            {t(l.sharedCatalog.filter.Include)}
          </Tabs.Header>
          <Tabs.Header
            tabId="exclude"
            isCustomChild
            onClick={() => setOpenTabId("exclude")}
            css={[
              styles.tabHeader,
              "exclude" === openTabId && styles.tabSelected,
              onlyIngredient && styles.disabledTab,
            ]}
          >
            {t(l.sharedCatalog.filter.Exclude)}
          </Tabs.Header>
        </Tabs.Headers>
        <Tabs.Content tabId="include">
          <IncludeIngredientsContent logFilterEvent={logFilterEvent} />
        </Tabs.Content>
        <Tabs.Content tabId="exclude">
          <ExcludeIngredientsContent logFilterEvent={logFilterEvent} />
        </Tabs.Content>
      </Tabs>
    </Accordion>
  );
};

export { Ingredients };
